<template>
  <b-overlay :show="loadingIndicator" rounded="sm" variant="dark">
    <b-container>
      <b-row class="vh-100" align-h="center">
        <b-col align-self="center" cols="12" sm="9" lg="5">
          <b-card v-if="!createGroupSuccess && !showResendEmailVerficationMessage">
            <img class="webmag-logo-big" src="@/assets/logo-webmag-big.png" alt="webmag logo"/>
            <p> {{ $t('setup.desc') }}</p>
            <h4
              class="text-center"
              :style="{visibility: previewUrl ? 'visible' : 'hidden'}"
            >
              {{ previewUrl }}
            </h4>
            <b-form @submit.stop.prevent="onSubmit">
              <b-form-group id="group-name-group">
                <b-form-input
                  id="group-name-input"
                  name="group-name-input"
                  type="text"
                  :placeholder="$t('setup.placeholderInput')"
                  v-model.trim="$v.form.groupName.$model"
                  @input="showAndValidateSlug"
                  :state="validateState('groupName')"
                  aria-describedby="group-name-live-feedback"
                ></b-form-input>

                <b-form-invalid-feedback id="group-name-live-feedback">
                  <div class="error" v-if="!$v.form.groupName.required">
                    {{ $t('setup.requireName') }}
                  </div>
                  <div class="error" v-if="showDuplicateGroupError">
                    {{ $t('setup.duplicateName') }}
                  </div>
                  <div class="error" v-if="showBlacklistGroupError">
                    {{ $t('setup.restrictedName') }}
                  </div>
                </b-form-invalid-feedback>
              </b-form-group>
              <b-button
                block
                class="p-3"
                type="submit"
                variant="success"
                :disabled="$v.form.$invalid"
              >
                {{ $t('setup.createGroupButton') }}
              </b-button>
            </b-form>
          </b-card>
          <create-group-success
            v-if="createGroupSuccess && !showResendEmailVerficationMessage"/>
          <verification-email-send-card v-if="verificationEmailSent"/>
        </b-col>
      </b-row>
    </b-container>
  </b-overlay>
</template>

<script>
import CreateGroupSuccess from '@/components/messages/CreateGroupSuccessCard.vue';
import VerificationEmailSendCard from '@/components/messages/VerificationEmailSendCard.vue';
import corporateIdentityDefaultFolderData from '@/defaultData/corporateIdentityDefaultFolderData';
import DuplicateSharedTemplate from '@/mixins/duplicateSharedTemplate';
import SendAnnouncementEmail from '@/graphQlQueries/mutations/sendInviteEmail';
import GetLanguageIdByHandle from '@/graphQlQueries/queries/getLanguageIdByHandle';
import CheckForDuplicateSlug from '@/graphQlQueries/queries/checkForDuplicateSlug';
import GetAllGroupsOfUserByUserId from '@/graphQlQueries/queries/getAllGroupsOfUserByUserId';
import SetupEditorUser from '@/graphQlQueries/mutations/setupEditorUser';
import SetPageTypeInCache from '@/graphQlQueries/mutations/setPageTypeInCache';
import SendPublishedDataToServer from '@/mixins/sendPublishedDataToServer';
import Auth from '@aws-amplify/auth';
import Login from '@/mixins/login';
import slugify from '@sindresorhus/slugify';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

const duplicateGroupValidator = (slugIsUnique) => (value) => {
  if (!value) return false;
  return slugIsUnique;
};

const blacklistedGroupNames = [
  'aktion', 'alpha', 'app', 'beta', 'blog', 'book', 'broschuere', 'career', 'case', 'cases', 'christian', 'click', 'contact', 'content', 'corona', 'customers', 'demo', 'diy', 'doityourself', 'edit', 'editor', 'faq', 'features', 'fellow', 'fellowship', 'free', 'fuck', 'galery', 'germany', 'global', 'help', 'hilfe', 'home', 'hub', 'inside', 'intern', 'job', 'jobs', 'karriere', 'katalog', 'kontakt', 'kunden', 'learn', 'legal', 'login', 'magazin', 'magazine', 'marketing', 'media', 'medien', 'member', 'membership', 'neu', 'news', 'operations', 'plattform', 'portal', 'press', 'presse', 'pub', 'publications', 'publish', 'register', 'roadmap', 'sales', 'service', 'services', 'show', 'social', 'special', 'specials', 'start', 'studie', 'sub', 'success', 'support', 'team', 'updates', 'usecase', 'video', 'webinar', 'webinars', 'webmag', 'webmag', 'welcome', 'whitelabel', 'whitepaper', 'work', 'you', 'prognos-roche', 'pferdewirtontout', 'wildermohn', 'wild', 'amb-special2022', '_webmag_default', '1e9', '1und1', '23degrees', '52ways', 'a-bolte', 'abg-demo', 'abg-koerner', 'abg-marketing', 'adint', 'agrimag', 'alexander-arcadis', 'amsmarketing-caseih', 'amsmarketing-caseih_1', 'amsmarketing-nh', 'amsmarketing-nh_1', 'amsmarketing-steyr', 'amsmarketing-steyr_1', 'arcadis', 'arcadis-eckardt', 'arcadis-goedicke', 'arcadis-nv', 'arcadis1', 'asd-dispute-resolution', 'asd-law', 'asd-law-antitrust', 'asd-law-chabert', 'asd-law-commercial', 'asd-law-compliance', 'asd-law-corporate-finance-ma', 'asd-law-culture', 'asd-law-dispute-resolution', 'asd-law-energy', 'asd-law-inside', 'asd-law-insurance', 'asd-law-ip', 'asd-law-labour-employment', 'asd-law-maritime', 'asd-law-news', 'asd-law-public', 'asd-law-realestate', 'asd-law-sports', 'asd-law-tal', 'asd-law-tax', 'asd-law-technology-media', 'asd-law-template', 'asd-test-elke-krell', 'automaten-forum', 'batten-company', 'beta-customer', 'bia', 'brandseed', 'bts-ilaria-scaglia', 'bts-test-alessia-pinna', 'bts-test-fabio-bertini', 'bts-test-francesco-colletto', 'burak', 'c13', 'camelot-mc', 'cases', 'cce', 'censhare', 'christosmith', 'claus', 'clickstorm', 'clutch-magazin', 'cnhi-agency-barbara-alcazar', 'cnhi-agency-paloma-clm', 'cnhi-agrimag', 'cnhi-agroconcept-romania', 'cnhi-blueandyou', 'cnhi-bruns', 'cnhi-caracterazul', 'cnhi-dealer-capobianco', 'cnhi-dealer-consorzioagrarioravenna', 'cnhi-dealer-dvftraktors', 'cnhi-dealer-lacommercialeagricola', 'cnhi-dealer-martinengo', 'cnhi-dealer-ricambimarcantuono', 'cnhi-dealer-rolmax', 'cnhi-deimel', 'cnhi-files', 'cnhi-flashinfo', 'cnhi-gruber', 'cnhi-impulsorojo', 'cnhi-kloster', 'cnhi-ltzlandtechnik', 'cnhi-maxmag', 'cnhi-maxpromo', 'cnhi-newtec', 'cnhi-ostermayr', 'cnhi-partsblue', 'cnhi-pichler', 'cnhi-pst-sales', 'cnhi-raccoltaricambi', 'cnhi-redefine', 'cnhi-ricambi-incampo', 'cnhi-steyraktuell', 'cnhi-steyrcenternord', 'cnhi-teileaktuell', 'cnhi-teileforum', 'cnhi-test', 'cnhi-test-alexandre-raymond', 'cnhi-test-andra-codreanu', 'cnhi-test-andrey-nikitenko', 'cnhi-test-camielia-tanase', 'cnhi-test-cecilie-holm', 'cnhi-test-chau-ngothihong', 'cnhi-test-daniela-bocci', 'cnhi-test-dariusz-marciniak', 'cnhi-test-elisa-cala', 'cnhi-test-fabien-roth', 'cnhi-test-georgina-kreysa', 'cnhi-test-giovanni-derosa', 'cnhi-test-helene-goemaere', 'cnhi-test-henrik-hansen', 'cnhi-test-hugo-martinet', 'cnhi-test-jana-klaiber', 'cnhi-test-jesus-maudes', 'cnhi-test-jona-hormann', 'cnhi-test-kaczanowska', 'cnhi-test-lars-nielsen', 'cnhi-test-lluis-masdeuduro', 'cnhi-test-lucamassimo-raccagni', 'cnhi-test-marcin-dobrzynski', 'cnhi-test-martin-muehringer', 'cnhi-test-natalia-kierzkowska', 'cnhi-test-pauline-ringuede', 'cnhi-test-peterphilipp-hampel', 'cnhi-test-ramona-katz', 'cnhi-test-raquel-vacas', 'cnhi-test-sarah-kallies', 'cnhi-test-solene-bouba-olga', 'cnhi-test-sylvie-priday', 'cnhi-test-ute-possemeyer', 'cnhi-titan-bulgaria', 'cnhi-titan-romania', 'cnhi-titan-serbia', 'cnhi-wmgottenheim', 'cnhi-yellowblueandyou', 'coexist-collective', 'criteo', 'cs-dach', 'cui', 'd-art-design', 'de', 'deinklub', 'deluxemallorca', 'demo', 'derrundetisch', 'dev', 'diegestalter', 'digipaper', 'digit', 'donetrainings', 'dreibrueder', 'drivestore', 'easy-software', 'easy-software-content', 'easyjournal', 'eehh', 'en', 'etoro', 'eva', 'everyoneplus', 'fachwelt', 'fachwelt-demo', 'fachwelt-pharmatechnik', 'fachwelt-wasser-abwassertechnik', 'fanbike', 'ffi-verlag', 'filestage', 'filestage1', 'foodboom', 'fou', 'funk', 'funk-bbt', 'funk2', 'gastromatic', 'hac', 'hansainvest', 'hansainvest-marketing', 'haufe-akademie', 'haufe-akademie01', 'haufe-akademie02', 'haufe-akademie03', 'haufe-akademie04', 'haufe-akademie05', 'hausvorteil', 'heikopaiko', 'heinmedia', 'hi5', 'hm-media', 'hockeyscouts', 'hugu', 'huz', 'huz2', 'huzevents', 'idg', 'ifok-mons', 'ifok-rueckziegel', 'ifok-wagner', 'ihklei', 'ippen', 'ippen-digital', 'irt', 'insignts', 'join', 'kadiraslan', 'kaefer', 'keditor-customer', 'keditor_customer', 'kerngedanke', 'klient1', 'koelliker', 'lenaxxx', 'lhlk', 'lkj', 'lughni', 'luisaquirmbach', 'mamistravelguide', 'marcus-meistro', 'marcusmeistro', 'Marisa-Kuehn', 'matt', 'matthias', 'mcleod-services', 'medialab', 'medialab-pia', 'meistro', 'methodworks', 'mietwagen-check', 'miteinander', 'montanus-easy', 'msc-de', 'naurod', 'navos', 'navos-kolla', 'neulandherzer', 'ngkntk', 'ohwoman', 'parix', 'patti-easy', 'photomapix', 'presigno', 'prognos', 'prognos-demo', 'prognos-gdv', 'prognos-studien', 'prognos-vbwreport', 'prognos-vodafone', 'prx', 'prxstarter', 'qwe', 'rachel-media', 'rixen', 'rundstedt', 'sabine', 'safari', 'schmidt-easy', 'schneider-easy', 'schoenhardt', 'schoenknecht', 'schumacher-easy', 'sdfsdf', 'sevenone', 'sidepreneur', 'solveigerlat', 'solveigwust', 'sos-kinderdorf-zoom', 'spacekommunikation2021', 'sparkasse-unna-kamen', 'sparkasse-unnakamen', 'spec', 'spiegel', 'spk-swb', 'spk-swb-public', 'spotlight', 'stade-landesbergen', 'stade-landesbergen-tennet', 'stadtteilzentrum', 'stadtteilzentrumsteglitz', 'stalatennet', 'studiogong', 'suedlink', 'support', 'suw', 'swmh', 'sz', 'szs', 'tanaeg', 'techmahindra', 'tennet', 'tennet-barth', 'tennet-braun', 'terra-verde-africa', 'test', 'test-david', 'test123', 'testbitteloeschen', 'testdante10', 'tester1', 'tester1a', 'tester2', 'tester3', 'tester4', 'tester01', 'tester04', 'testmag', 'testnew02', 'testnew03', 'testnew04', 'testnew11', 'testprem', 'testthebest', 'thomas-satori', 'tietsche-easy', 'tobiastiefert', 'tradedoubler', 'transparent', 'tryout', 'upig', 'valvisio', 'vangardist', 'videomag', 'volkerschad', 'vroniccc', 'wahle-mecklar', 'wayray', 'webinar2021', 'webmag_backup', 'webmagkaefer', 'wellhausboote', 'wirecard', 'wlv', 'wolffberg', 'xello', 'xerox', 'xiting', 'xplr', 'xplr-media', 'xyxywxkwiniqsqwsw', 'yellowblue', 'zaid', 'zeditorial', 'zenmate', '1741', 'miq', 'complex-glowing',
];

const blacklistGroupValidator = (value) => {
  if (!value) return false;
  const slugifiedValue = slugify(value);
  return !blacklistedGroupNames.includes(slugifiedValue);
};

export default {
  name: 'Setup',
  components: { VerificationEmailSendCard, CreateGroupSuccess },
  mixins: [validationMixin, SendPublishedDataToServer, DuplicateSharedTemplate, Login],
  data() {
    return {
      form: {
        groupName: null,
      },
      userId: null,
      userEmail: null,
      slugifiedName: null,
      loadingIndicator: true,
      previewUrl: null,
      showDuplicateGroupError: null,
      showBlacklistGroupError: null,
      createGroupSuccess: false,
      showResendEmailVerficationMessage: false,
      verificationEmailSent: false,
      languageId: null,
      languageIdEng: null,
      slugIsUnique: false,
    };
  },
  async created() {
    setTimeout(() => {
      if (window.$crisp) {
        console.log('user registered in aws', window.$crisp.get('user:email'));
        console.log('user-registered', window.$crisp.get('session:data', 'user-registered'));
        console.log('last_group', window.$crisp.get('session:data', 'last_group'));
      }
    }, 2000);
    try {
      await this.$apollo.mutate({
        mutation: SetPageTypeInCache,
        variables: {
          type: 'setup-page',
        },
      });
      const awsUser = await Auth.currentAuthenticatedUser();
      if (awsUser && awsUser.attributes) {
        // check if email is verified
        if (!awsUser.attributes.email_verified) {
          await this.$router.push({
            path: '/oops-something-went-wrong',
            query: { error: 'emailNotConfirmed' },
          }).catch((err) => {
            throw new Error(`Routing Error occurred! ${err}.`);
          });
        } else {
          // the user and the session exists because of the router
          const currentSession = await Auth.currentSession();
          const idTokenPayload = JSON.parse(currentSession.getIdToken().payload['https://hasura.io/jwt/claims']);
          if (idTokenPayload['x-hasura-user-exists'] === 'true') {
            // make a query to db and check for the users
            this.userId = Math.floor(idTokenPayload['x-hasura-user-id']);
            const { data: { users: [hasuraUser] } } = await this.$apollo.query({
              query: GetAllGroupsOfUserByUserId,
              variables: {
                userId: this.userId,
              },
            });
            if (hasuraUser
              && (hasuraUser.users_roles_groups.length || hasuraUser.users_roles_teams.length)
            ) {
              // the user is already in a group or a team, so we don't do the setup anymore!
              await this.$router.push('/').catch((err) => {
                throw new Error(err);
              });
            }
          }
        }
        this.userEmail = awsUser.attributes.email;
        this.userTelephone = awsUser.attributes.middle_name;
        this.loadingIndicator = false;
      } else {
        // user has to do a challenge so send him back to login
        this.$router.push('/webmag-login').catch((err) => {
          throw new Error(`Routing Error on setup page occurred! ${err}.`);
        });
        this.loadingIndicator = false;
      }
    } catch (e) {
      console.log(e);
      this.$router.push('/webmag-login').catch((err) => {
        throw new Error(`Routing Error on setup page occurred! ${err}.`);
      });
      this.loadingIndicator = false;
    }
  },
  apollo: {
    languageIdEng: {
      query: GetLanguageIdByHandle,
      variables: {
        handle: 'en',
      },
      update(data) {
        return data.languages[0].language_id;
      },
    },
  },
  validations() {
    return {
      form: {
        groupName: {
          required,
          duplicateGroup: duplicateGroupValidator(this.slugIsUnique),
          blacklistGroup: blacklistGroupValidator,
        },
      },
    };
  },
  methods: {
    async getUserLanguageId() {
      const userLangBrowserString = navigator.language || navigator.userLanguage;
      const userLang = (userLangBrowserString.indexOf('-') !== -1)
        ? userLangBrowserString.substring(0, userLangBrowserString.indexOf('-'))
        : userLangBrowserString;
      const { data: { languages: [language] } } = await this.$apollo.query({
        query: GetLanguageIdByHandle,
        variables: {
          handle: userLang,
        },
      });
      const userLanguageId = language ? language.language_id : this.languageIdEng;
      return userLanguageId || null;
    },
    async showAndValidateSlug(name) {
      this.showDuplicateGroupError = false;
      this.showBlacklistGroupError = false;
      this.prepareThePreviewLink(name);
      if (name !== '') {
        // query with the configAvailable action
        const {
          data: {
            configAvailable: { available: slugIsUnique },
          },
        } = await this.$apollo.query({
          query: CheckForDuplicateSlug,
          variables: {
            slug: this.slugifiedName,
          },
        });
        this.slugIsUnique = slugIsUnique;
        if (!slugIsUnique) {
          this.showDuplicateGroupError = true;
        }
        if (blacklistedGroupNames.includes(this.slugifiedName)) {
          this.showBlacklistGroupError = true;
        }
      }
    },
    prepareThePreviewLink(name) {
      this.slugifiedName = slugify(name);
      const { VUE_APP_DOMAIN } = process.env;
      // replace [group_slug] with group slug
      this.previewUrl = VUE_APP_DOMAIN.replace('[group_slug]', this.slugifiedName);
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    async onSubmit() {
      await this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      const name = this.form.groupName;
      const slug = this.slugifiedName;
      const domain = process.env.VUE_APP_DOMAIN.replace('[group_slug]', slug);
      let dataForServer = null;

      this.loadingIndicator = true;
      // publish the group folder
      dataForServer = {
        action: 'createGroup',
        data: {
          group_slug: slug,
          group_domain: domain,
        },
      };

      const publishedDataWasSuccessfulSend = await this
        .sendPublishedDataToServer(dataForServer);
      if (publishedDataWasSuccessfulSend) {
        // set up the hasura user with the group
        try {
          const userLanguageId = await this.getUserLanguageId();
          const cognitoUser = await Auth.currentAuthenticatedUser();
          const fullName = cognitoUser.attributes.name;
          const telephone = cognitoUser.attributes.middle_name;

          const { data: { setupEditorUser: { group_id } } } = await this.$apollo.mutate({
            mutation: SetupEditorUser,
            variables: {
              obj: {
                email: this.userEmail,
                telephone,
                group_name: name,
                group_domain: domain,
                group_slug: slug,
                full_name: fullName,
                language_id: userLanguageId,
                corporate_identity: JSON.stringify(corporateIdentityDefaultFolderData),
                email_confirmed: true,
              },
            },
          });
          console.log('group id after insert the data', group_id);
          const { refreshToken } = cognitoUser.getSignInUserSession();
          // eslint-disable-next-line no-unused-vars
          cognitoUser.refreshSession(refreshToken, async (error, session) => {
            // after the token is refreshed set the user to administrator
            this.$currentUserRole = 'administrator';
            try {
              // add a demo project to the group
              await this.duplicateWhitePaperProject(group_id);
              const user_email = this.userEmail;
              await this.$apollo.mutate({
                mutation: SendAnnouncementEmail,
                variables: {
                  subject: this.$t('register.announcementToWebmag.subjectSetup', { user_email }),
                  to: 'support@webmag.io',
                  html: this.$t('register.announcementToWebmag.textSetup', {
                    user_name: fullName,
                    telephone,
                    user_email,
                    user_account: name,
                    user_domain: domain,
                  }),
                },
              });
              fetch('https://hooks.slack.com/services/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', {
                method: 'POST',
                body: JSON.stringify({
                  text: this.$t('register.announcementToWebmag.slackMessage', {
                    user_name: fullName,
                    telephone,
                    user_email,
                    user_account: name,
                    user_domain: domain,
                  }),
                }),
              })
                .then((response) => {
                  if (!response.ok) {
                    throw new Error('Network response was not ok.');
                  }
                  console.log('Slack message sent successfully!');
                })
                .catch((error2) => {
                  console.error('Error:', error2);
                });

              // after success display a 'thank you' message with button to dashboard
              this.createGroupSuccess = true;
            } catch (err) {
              console.error('Mutation Error SendAnnouncementEmail for SetupEditorUser', err);
            }
            this.loadingIndicator = false;
          });
        } catch (err) {
          console.error('Mutation Error SetupEditorUser', err);
          this.loadingIndicator = false;
        }
      }
    },
  },
};
</script>

<style scoped>
h2 {
  margin-bottom: 20px;
}
</style>
